/**
 * @wl/widget-mortgage — чистые функции расчётов.
 *
 * Аннуитетная формула. Здесь — клиентский расчёт для instant feedback в UI.
 * Источник истины для CRM — СЕРВЕРНЫЙ пересчёт по bank_rates при insert lead
 * (apps/api/src/routes/public/leads.ts → recomputeMonthly): клиентскому
 * monthlyPayment сервер не доверяет и перезаписывает его своим значением.
 */

import { formatNum } from './format';

/**
 * Аннуитетный платёж.
 *
 * @param principal — сумма кредита (стоимость - первый взнос), ₽
 * @param annualRate — годовая ставка, % (например 6.0)
 * @param months — срок в месяцах
 * @returns ежемесячный платёж в ₽, округление до целого делает caller
 */
export function annuityPayment(principal: number, annualRate: number, months: number): number {
  if (principal <= 0 || months <= 0) return 0;
  if (annualRate <= 0) return principal / months;
  const r = annualRate / 100 / 12;
  const pow = Math.pow(1 + r, months);
  return (principal * r * pow) / (pow - 1);
}

/**
 * Удобный wrapper для UI: считает платёж по году срока и ставке для конкретного банка.
 */
export function calculateForBank(
  cost: number,
  downPayment: number,
  termYears: number,
  annualRate: number,
): number {
  const principal = Math.max(0, cost - downPayment);
  return annuityPayment(principal, annualRate, termYears * 12);
}

/**
 * Максимальный first-pay в % от стоимости.
 * Дефолт — 90 % (как в прототипе v2; банковский максимум обычно тоже до 90 %).
 */
export function maxDownPaymentPct(): number {
  return 90;
}

/** Минимальный first-pay в % (банковский гейт). */
export function minDownPaymentPct(): number {
  return 10;
}

/**
 * Форматирование числа в русском стиле (тонкие пробелы как разделители тысяч).
 */
export function formatRub(n: number): string {
  return Math.round(n).toLocaleString('ru-RU');
}

/**
 * Окончание слова «год / года / лет» по числу.
 */
export function termSuffix(years: number): string {
  if (years === 1) return 'год';
  if (years >= 2 && years <= 4) return 'года';
  return 'лет';
}

/**
 * Полная стоимость кредита (ПСК) по 353-ФЗ ст.6.
 *
 * Решает уравнение Σ ДПk / (1 + i)^Sk = 0 относительно ставки базового периода i
 * (месяц) и приводит к годовому виду: ПСК = i × ЧБП × 100 %, ЧБП = 12.
 *
 * Денежные потоки заёмщика:
 *   t = 0           : +(principal − commission)   — фактически получено на руки
 *   t = 1..months   : −payment                    — аннуитет, рассчитанный от principal
 *
 * Метод — bisection по монотонно возрастающей NPV(i): надёжно сходится за ≤60 шагов.
 *
 * @param principal         сумма кредита (стоимость − первый взнос), ₽
 * @param annualRate        номинальная годовая ставка банка, %
 * @param months            срок кредита в месяцах
 * @param commissionPct     разовая комиссия банка при выдаче, % от principal (0 если нет)
 * @returns                 ПСК в % годовых (например 6.214). 0 если входные невалидны.
 */
export function computePsk(
  principal: number,
  annualRate: number,
  months: number,
  commissionPct: number = 0,
): number {
  if (principal <= 0 || months <= 0) return 0;

  const payment = annuityPayment(principal, annualRate, months);
  if (payment <= 0) return 0;

  const commission = (principal * Math.max(0, commissionPct)) / 100;
  const netDisbursed = principal - commission;
  if (netDisbursed <= 0) return 0;

  // NPV(i) для месячной ставки i. Монотонно возрастает по i.
  const npv = (i: number): number => {
    if (i === 0) return netDisbursed - payment * months;
    const factor = (1 - Math.pow(1 + i, -months)) / i;
    return netDisbursed - payment * factor;
  };

  // При i=0 NPV ≤ 0 (без комиссии = 0; с комиссией < 0). При больших i NPV растёт.
  // Верхняя граница 1.0 (100 %/мес ≈ 1200 % годовых) с запасом покрывает любые сценарии.
  let lo = 0;
  let hi = 1;
  if (npv(hi) < 0) return hi * 12 * 100; // защитный fallback, в реальности недостижимо

  for (let iter = 0; iter < 64; iter++) {
    const mid = (lo + hi) / 2;
    const v = npv(mid);
    if (v < 0) lo = mid;
    else hi = mid;
    if (hi - lo < 1e-10) break;
  }
  return ((lo + hi) / 2) * 12 * 100;
}

/**
 * Месячная ставка i, при которой дисконтированные платежи графика равны сумме,
 * полученной на руки: Σ платёж_k / (1 + i)^k = netDisbursed.
 *
 * Тот же приём, что в computePsk: бисекция по монотонно возрастающей NPV(i),
 * те же границы [0; 1] и тот же критерий остановки. Разница одна — платежи
 * берутся из массива, а не из закрытой формулы аннуитета, поэтому метод годится
 * для кредита с переменным платежом (льготные 15 лет, потом выше).
 */
function solveMonthlyRate(netDisbursed: number, payments: readonly number[]): number {
  // NPV(i) = полученная сумма − Σ платёж_k · v^k, v = 1/(1+i). Монотонно растёт по i.
  const npv = (i: number): number => {
    const v = 1 / (1 + i);
    let disc = 1;
    let pv = 0;
    for (let k = 0; k < payments.length; k++) {
      disc *= v;
      pv += payments[k]! * disc;
    }
    return netDisbursed - pv;
  };

  let lo = 0;
  let hi = 1;
  if (npv(hi) < 0) return hi; // защитный fallback, как в computePsk
  for (let iter = 0; iter < 64; iter++) {
    const mid = (lo + hi) / 2;
    if (npv(mid) < 0) lo = mid;
    else hi = mid;
    if (hi - lo < 1e-10) break;
  }
  return (lo + hi) / 2;
}

/**
 * ПСК по ПРОИЗВОЛЬНОМУ графику платежей (353-ФЗ ст.6), % годовых.
 *
 * computePsk умеет только аннуитет с одним платежом на весь срок. «Семейная
 * ипотека» с 01.10.2026 — кредит с переменным платежом: первые 15 лет ставка
 * льготная, дальше растёт, а часть суммы сверх лимита идёт по рыночной ставке.
 * Формула одной ставки на таком графике занизила бы ПСК, поэтому считаем по
 * фактическим потокам.
 *
 * Для графика из одинаковых платежей результат совпадает с computePsk
 * (проверяется тестом test/psk-schedule.test.ts).
 *
 * @param principal      сумма кредита, ₽
 * @param payments       платежи по месяцам: payments[0] — первый месяц
 * @param commissionPct  разовая комиссия банка при выдаче, % от principal
 * @returns              ПСК в % годовых; 0, если входные невалидны
 */
export function computePskBySchedule(
  principal: number,
  payments: readonly number[],
  commissionPct: number = 0,
): number {
  if (principal <= 0 || payments.length === 0) return 0;
  if (!payments.some((p) => p > 0)) return 0;

  const commission = (principal * Math.max(0, commissionPct)) / 100;
  const netDisbursed = principal - commission;
  if (netDisbursed <= 0) return 0;

  return solveMonthlyRate(netDisbursed, payments) * 12 * 100;
}

/**
 * Диапазон ПСК [min, max] по графику платежей — то же «без страховки — со
 * страховкой», что в computePskRange: к каждому платежу добавляется равномерная
 * надбавка за страхование ≈ средний остаток (principal / 2) × годовая премия / 12.
 */
export function computePskRangeBySchedule(
  principal: number,
  payments: readonly number[],
  commissionPct: number = 0,
  insurancePctAnnual: number = DEFAULT_INSURANCE_PCT_ANNUAL,
): PskRange {
  const min = computePskBySchedule(principal, payments, commissionPct);

  if (principal <= 0 || payments.length === 0 || insurancePctAnnual <= 0) {
    return { min, max: min };
  }

  const extraMonthly = (principal / 2) * (insurancePctAnnual / 100) / 12;
  const withInsurance = payments.map((p) => p + extraMonthly);
  const commission = (principal * Math.max(0, commissionPct)) / 100;
  const netDisbursed = principal - commission;
  if (netDisbursed <= 0 || !withInsurance.some((p) => p > 0)) return { min, max: min };

  const max = solveMonthlyRate(netDisbursed, withInsurance) * 12 * 100;
  return { min, max: Math.max(max, min) };
}

/**
 * Форматирование ПСК для отображения: «6,214 %».
 * ЦБ публикует ПСК с 3 знаками — придерживаемся того же, но пустые знаки
 * не печатаем: «6,500 %» человек читает как «6,5 %».
 */
export function formatPskPct(psk: number): string {
  return `${formatNum(psk, 3)} %`;
}

/**
 * Типовое суммарное удорожание из-за обязательного страхования и сопутствующих услуг
 * (страхование жизни ~0.5 % + страхование имущества ~0.15 % + прочие банковские услуги ~0.6 %)
 * — в % годовых от остатка задолженности. Используется для верхней границы ПСК.
 *
 * 1.25 % — рыночное среднее для РФ на 2026 г. (см. тарифы Сбер/ВТБ/Альфа).
 * При точном значении из договора банка — клиент перебивает через override в админке (TODO).
 */
const DEFAULT_INSURANCE_PCT_ANNUAL = 1.25;

export interface PskRange {
  /** Нижняя граница ПСК — без обязательного страхования и доп. услуг. */
  min: number;
  /** Верхняя граница ПСК — с типовым набором обязательных платежей. */
  max: number;
}

/**
 * Диапазон ПСК [min, max] по 353-ФЗ ст.6.
 *
 * min — без страхования (только ставка + разовая комиссия за выдачу).
 * max — с типовыми обязательными платежами по страхованию жизни / имущества
 *       и сопутствующим услугам банка (DEFAULT_INSURANCE_PCT_ANNUAL % годовых от остатка).
 *
 * Реализация max — упрощённая: эквивалент равномерной добавки к ежемесячному платежу,
 * пропорциональной среднему остатку (principal/2). Погрешность <0.1 п.п. против полного
 * пересчёта с пересчётом остатка ежемесячно, что приемлемо для MVP-калькулятора.
 */
export function computePskRange(
  principal: number,
  annualRate: number,
  months: number,
  commissionPct: number = 0,
  insurancePctAnnual: number = DEFAULT_INSURANCE_PCT_ANNUAL,
): PskRange {
  const min = computePsk(principal, annualRate, months, commissionPct);

  if (principal <= 0 || months <= 0 || insurancePctAnnual <= 0) {
    return { min, max: min };
  }

  // Доп. ежемесячный платёж за страхование/услуги ≈ средний остаток × годовая премия / 12.
  // Средний остаток для аннуитета ≈ principal / 2 — стандартная аппроксимация.
  const extraMonthly = (principal / 2) * (insurancePctAnnual / 100) / 12;
  const basePayment = annuityPayment(principal, annualRate, months);
  const totalPayment = basePayment + extraMonthly;
  const commission = (principal * Math.max(0, commissionPct)) / 100;
  const netDisbursed = principal - commission;

  if (totalPayment <= 0 || netDisbursed <= 0) return { min, max: min };

  const npv = (i: number): number => {
    if (i === 0) return netDisbursed - totalPayment * months;
    const factor = (1 - Math.pow(1 + i, -months)) / i;
    return netDisbursed - totalPayment * factor;
  };

  let lo = 0;
  let hi = 1;
  if (npv(hi) < 0) return { min, max: hi * 12 * 100 };

  for (let iter = 0; iter < 64; iter++) {
    const mid = (lo + hi) / 2;
    const v = npv(mid);
    if (v < 0) lo = mid;
    else hi = mid;
    if (hi - lo < 1e-10) break;
  }
  const max = ((lo + hi) / 2) * 12 * 100;
  return { min, max: Math.max(max, min) };
}

/**
 * Форматирование ПСК-диапазона для отображения: «6—6,875 %».
 * Если min ≈ max (разница <0.005 п.п.) — показываем одно число.
 */
export function formatPskRange(range: PskRange): string {
  if (range.max - range.min < 0.005) return `${formatNum(range.min, 3)} %`;
  return `${formatNum(range.min, 3)}—${formatNum(range.max, 3)} %`;
}
